// studyFlow/studySetReturn.ts
//
// The way back from a study set to the conversation that served it (ORA-681).
//
// Two destinations, decided here rather than in the flow hook so the rule can
// be read and tested on its own:
//
//   - the full chat page, addressable by URL, which is what a set launched from
//     `/chat` returns to and what everything unsupported falls back to;
//   - the page the student was on, with the companion reopened over it — the
//     drawer, the mobile sheet or the floating shell, whichever that route
//     gets. Which chrome appears is `AIDrawerRenderer`'s decision, so a launch
//     from the shell over the dashboard comes back to the shell.
//
// A `document_split` chat (the retired document overlay's, from a return
// persisted before ORA-736) is not addressable, and an origin that is itself a
// study route would send the student straight back into a session, so both
// take the fallback.
//
// Two more take it (staging smoke, 2026-09-17): an origin that IS the chat page
// — reopening a drawer over it would drop the `blockId` the page needs to
// scroll, and the page is the thread anyway — and a companion that cannot
// actually be reopened. The last one is what a cold return hits: the thread
// URL is opened in a browser that has only the persisted study context, so
// nothing in this app instance has the conversation, and `open()` would land
// the student on the origin page with no companion over it.

import type { StudySetChatReturn } from '../slices/studyContext/studyContextSlice';
import { studySessionPathOf } from '../components/ora/Chat/Companion/blocks/studySetSession';

export type StudySetReturnDestination =
  | { chrome: 'chat_page'; to: string }
  | {
      chrome: 'drawer';
      to: string;
      conversationId: string;
      scrollToBlockId: string;
    };

/**
 * The addressable thread: the AI desktop at `/`. An origin on it returns to
 * it, never to a drawer. `/chat` is its old address (it redirects there).
 */
const CHAT_PAGE_PATHS = ['/', '/chat'];

/** The desktop on the conversation, with the block to scroll to. */
export const chatPageReturnPath = (
  conversationId: string,
  blockId: string
): string =>
  `/?conversationId=${encodeURIComponent(conversationId)}&blockId=${encodeURIComponent(blockId)}`;

export const resolveStudySetReturn = ({
  chatReturn,
  returnPath,
  returnSearch,
  canReopenCompanion = true,
}: {
  chatReturn: StudySetChatReturn;
  returnPath: string | null;
  returnSearch: string | null;
  /**
   * Whether the companion can actually be reopened over the origin page: a
   * drawer provider to ask, an account the companion is on for, and a thread
   * this app instance has. False takes the addressable page instead.
   */
  canReopenCompanion?: boolean;
}): StudySetReturnDestination => {
  const fallback: StudySetReturnDestination = {
    chrome: 'chat_page',
    to: chatPageReturnPath(chatReturn.conversationId, chatReturn.blockId),
  };

  if (chatReturn.surface !== 'drawer') return fallback;
  if (!canReopenCompanion) return fallback;
  if (!returnPath) return fallback;
  if (studySessionPathOf(returnPath)) return fallback;
  if (CHAT_PAGE_PATHS.includes(returnPath.split('?')[0])) return fallback;

  return {
    chrome: 'drawer',
    to: `${returnPath}${returnSearch ?? ''}`,
    conversationId: chatReturn.conversationId,
    scrollToBlockId: chatReturn.blockId,
  };
};
